import React, {useState, useEffect} from 'react';
import MotifSelect from '@ey/motif-react-wrapper/Select';
import MotifOption from '@ey/motif-react-wrapper/Option';
import MotifLabel from '@ey/motif-react-wrapper/Label';
import MotifSearch from '@ey/motif-react-wrapper/Search';
import MotifFormField from '@ey/motif-react-wrapper/FormField';
import MotifTextArea from '@ey/motif-react-wrapper/TextArea';
import MotifErrorMessage from '@ey/motif-react-wrapper/ErrorMessage';
import {useSelector, useDispatch} from 'react-redux';
import {
	successMessageDisplayDuration,
	currentResource,
	labels,
	cacheKeyRegex
} from '../../../util/uiconstants';
import Toast from '../../Common/Toast/Toast';
import Loader from '../../Common/Loaders/loadingindicator';
import AccessDeniedMessage from '../../Common/AccessDeniedMessage/AccessDeniedMessage';
import {
	getCache,
	deleteCache,
	clearResult
} from '../../../actions/admin/cacheaction';
import env from '../../../util/env';
import styled from 'styled-components';
import { getLocalizedAPIError, validateTicketNumber } from '../common/util';
import { clearErrors } from '../../../actions/erroractions';
import { setIsModalOpen } from '../../../actions/modalstateactions';
import TicketModal from '../common/ticketmodal';
import Ellipse from '@ey/canvascoreservices/Ellipse';
import MotifButton from '@ey/motif-react-wrapper/Button';
import EllipsesControl from '../../Common/EllipsesControl/EllipsesControl';

const Wrapper = styled.section`
	padding-top: var(--spacing-xx-large);
	.toastMessage {
		width: 100% !important;
		position: relative;
		.motif-toast {
			position: absolute;
			z-index: 9999;
		}
	}

	.cacheresult {
		height: calc(100vh - var(--px-370));
		position: relative;

		.result {
			.textarea {
				.motif-text-area {
					height: calc(100vh - var(--px-450));
					resize: none;
				}
			}

			.removecache {
				display: flex;
				justify-content: end;
				padding: var(--spacing-x-large) 0;
			}
		}

		.noresult {
			height: calc(100vh - var(--px-370));
			display: inline-block;
			width: 100%;
			display: flex;
			justify-content: center;
			flex: 0 auto;
			align-items: center;
			font-size: var(--px-16);
			color: var(--neutrals-700);
		}
	}

	.cachewrapper {
		display: flex;
		gap: var(--spacing-large);;

		.cache-dropdown-wrap {
			width: 20%;
		}

		.engId {
			width: 30%;
		}
		.cachekey {
			width: 50%;
		}
	}

	.getcacheclearcache {
		display: flex;
		gap: var(--spacing-large);
		padding: var(--spacing-x-large) 0;
	}
`;

function CacheDetails() {
	const userPermissions = useSelector((store) => store.userPermissions);
	const dispatch = useDispatch();
	const duration = successMessageDisplayDuration;

	const cache = useSelector((store) => store.cache);
	const [cacheKey, setCacheKey] = useState('');
	const [engagementId, setEngagementId] = useState('');
	const [cacheValue, setCacheValue] = useState('');
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [loading, setLoading] = useState(false);
	const [showNoResult, setShowNoResult] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [currentValue, setCurrentValue] = useState(currentResource.cacheOptionValue[0].value);
	const [cacheRemoveSuccessMessage, setCacheRemoveSuccessMessage] = useState('');
	const [showInvalidEngIdValidationMessage, setShowInvalidEngIdValidationMessage] = useState(false);
	const [isRemoveCacheDisabled, setIsRemoveCacheDisabled] = useState(false);
	const [showCacheModal, setShowCacheModal] = useState(false);
	const [showIndvalidCacheKeyValidationMessage, setShowInvalidCacheKeyValidationMessage] = useState(false);

	useEffect(() => {
		clearError(true);
		return () => {
			clearError(true);
		};
	}, []);

	const clearError = (isModelOpen) => {
		dispatch(clearErrors()).then(() => {
			dispatch(setIsModalOpen(isModelOpen));
		});
	};

	useEffect(() => {
		let cacheData = ''; 
		if (cache && Object.keys(cache).length > 0)
		{
			cacheData = JSON.stringify(cache)
		}
		setCacheValue(cacheData);
	}, [cache]);

	useEffect(() => {
		dispatch(clearResult());
	}, []);

	useEffect(() => {
		const timer = setTimeout(() => {
			setCacheRemoveSuccessMessage('');
		}, duration);
		return () => clearTimeout(timer);
	});

	const IsValidForm = () => {
		let isValid = true;

		// Validate engagement id only if LDC is selected
		if (currentValue == currentResource.cacheOptionValue[0].value) {
			if (!Number(engagementId)) {
				setShowInvalidEngIdValidationMessage(true);
				isValid = false;
			}
		}

		// Validate cache key
		if (cacheKey.trim().length === 0) {
			isValid = false;
		}
		else if (!cacheKeyRegex.test(cacheKey)) {
			setShowInvalidCacheKeyValidationMessage(true);
			isValid = false;
		}

		return isValid;
	};

	const onClearResultButtonClick = () => {
		setShowInvalidEngIdValidationMessage(false);
		setShowInvalidCacheKeyValidationMessage(false);
		setEngagementId('');
		setCacheKey('');
		dispatch(clearResult());
	};

	let cacheUri = `${env.getURL('serviceUrlV2')}admin/Cache?keyName=${encodeURIComponent(cacheKey.trim())}`;

	cacheUri += currentValue == currentResource.cacheOptionValue[0].value ?
					`&engagementId=${engagementId}&isGlobal=false` //LDC
					: '&isGlobal=true';							   //Universal	

	const onGetCacheButtonClick = () => {
		if (IsValidForm()) {
			setLoading(true);
			dispatch(getCache(cacheUri))
				.then(() => {
					setLoading(false);
					setShowNoResult(true);
				})
				.catch((e) => {
					setErrorMessage(getLocalizedAPIError(e));
					setShowNoResult(false);
				})
				.finally(() => {
					setLoading(false);
					setIsSubmitted(false);
				});
		} else {
			setIsSubmitted(true);
			setShowNoResult(false);
		}
		setIsRemoveCacheDisabled(false);
	};

	const onRemoveCacheButtonClick = (ticketNumber) => {
		const removeCacheUri = cacheUri + '&ticketnumber=' + encodeURIComponent(ticketNumber);
		setShowCacheModal(true);
		if (validateTicketNumber(ticketNumber)) {
			setLoading(true);
			dispatch(deleteCache(removeCacheUri))
				.then(() => {
					setCacheRemoveSuccessMessage(labels.cacheRemoveSuccessMessage);
					dispatch(clearResult()).then(() => setLoading(false));
				})
				.catch((e) => {
					setErrorMessage(getLocalizedAPIError(e));
				})
				.finally(() => {
					setLoading(false);
				});
		}
		setIsSubmitted(false);
		setShowNoResult(false);
	};

	function OnInputChange() {
		setIsRemoveCacheDisabled(true);
		if (cacheValue?.length > 0) {
			dispatch(clearResult());
		}
	}

	function adminPermissions() {
		let permissionGroupPermission = userPermissions?.permissions;
		return (
			permissionGroupPermission && permissionGroupPermission?.canReadDeleteCache
		);
	}

	const onConfirmClick = (enteredTicketNumber) => {
		onRemoveCacheButtonClick(enteredTicketNumber)
	};

	const formatOptionLabel = ({ value, label }) => (
		<Ellipse content={label} tooltip={label} noOfLines={1} />
	);

	const onSelectOptionChange = (option) => {
		setCurrentValue(option);
		if (option == currentResource.cacheOptionValue[1].value) {
			setEngagementId('');
		}
		dispatch(clearResult());
	};

	return (
		<Wrapper>
			{adminPermissions() ? (
				<section className="mainResponsiveWrapper">
					<section
						className="toastMessage"
						style={{
							display: cacheRemoveSuccessMessage.length > 0 ? 'block' : 'none'
						}}
					>
						<Toast
							variant="success"
							duration={duration}
							onClose={() => setCacheRemoveSuccessMessage('')}
						>
							{cacheRemoveSuccessMessage}
						</Toast>
					</section>

					<section
						className="toastMessage"
						style={{
							display: errorMessage.length > 0 ? 'block' : 'none'
						}}
					>
						<Toast variant="error" onClose={() => setErrorMessage('')}>
							{errorMessage}
						</Toast>
					</section>

					<section className="cachewrapper">
						<section className="cache-dropdown-wrap">
							<section className="cache-dropdown-section">
								<MotifFormField>
									<MotifLabel id="select-label" position="in">
										{labels.cacheSelection}
									</MotifLabel>
									<MotifSelect
										ariaLabelledBy="select-cache-location"
										labelPosition="in"
										onChange={(e)=>onSelectOptionChange(e)}
										value={currentValue.toString()}									>
										{currentResource.cacheOptionValue?.map((item) => (
											<MotifOption
												key={item.value}
												value={item.value.toString()}
												label={item.label}
											>
												<EllipsesControl content={item.label} tooltip={item.label} noOfLines={1} />
											</MotifOption>
										))}
									</MotifSelect>
								</MotifFormField>
							</section>
						</section>
						<section className="cachekey">
							<section>
								<MotifSearch
									maxLength={500}
									value={cacheKey}
									onChange={(event) => {
										setCacheKey(event.target.value.trim());
										setShowInvalidCacheKeyValidationMessage(false);
										OnInputChange();
									}}
									onEnter={onGetCacheButtonClick}
									placeholder={labels.cacheKey}
									title={cacheKey}
									onClear={() => {
										setCacheKey('');
									}}
								></MotifSearch>
								{cacheKey.trim().length === 0 && isSubmitted && (
									<MotifErrorMessage>
										{labels.validationMessage}
									</MotifErrorMessage>
								)}
								{cacheKey.trim().length !== 0 &&
									showIndvalidCacheKeyValidationMessage && (
										<MotifErrorMessage>
											{labels.adminModuleInvalidCacheKey}
										</MotifErrorMessage>
									)}
							</section>
						</section>

						<section className="engId">
							<section>
								<section>
									<MotifSearch
										maxLength={500}
										value={engagementId}
										disabled={
											currentValue ==
											currentResource.cacheOptionValue[1].value
												? true
												: false
										}
										onChange={(event) => {
											setEngagementId(event.target.value.trim());
											setShowInvalidEngIdValidationMessage(false);
											OnInputChange();
										}}
										onEnter={onGetCacheButtonClick}
										placeholder={labels.engagementID}
										onClear={() => {
											setEngagementId('');
										}}
									></MotifSearch>
									{engagementId.trim().length === 0 &&
										isSubmitted &&
										currentValue ==
											currentResource.cacheOptionValue[0].value && (
											<MotifErrorMessage>
												{labels.validationMessage}
											</MotifErrorMessage>
										)}
									{engagementId.trim().length !== 0 &&
										showInvalidEngIdValidationMessage && (
											<MotifErrorMessage>
												{labels.adminModuleinvalidEngagementId}
											</MotifErrorMessage>
										)}
								</section>
							</section>
						</section>						
					</section>
					<section className="getcacheclearcache">
							<MotifButton
								aria-label="getcache"
								variant="primary"
								size="medium"
								onClick={onGetCacheButtonClick}
							>
								{labels.getCache}
							</MotifButton>
							<MotifButton
								aria-label="clearcache"
								variant="secondary"
								size="medium"
								onClick={onClearResultButtonClick}
								disabled={cacheValue?.length === 0}
							>
								{labels.clearResult}
							</MotifButton>
						</section>
					<section className="cacheresult">
						{loading ? (
							<Loader show view="blockView" styleName="loadingstyle" />
						) : cacheValue?.length > 0 ? (
							<section className="result">
								<section className="textarea">
									<MotifFormField>
										<MotifTextArea value={cacheValue} hideClearButton/>
									</MotifFormField>
								</section>

								<section className="removecache button">
									<MotifButton
										aria-label="removecache"
										variant="secondary"
										size="medium"
										onClick={onRemoveCacheButtonClick}
										disabled={isRemoveCacheDisabled}
									>
										{labels.removeCache}
									</MotifButton>
								</section>

								<section className="showTicketModal">
									{showCacheModal ? (
										<TicketModal
											onHide={setShowCacheModal}
											onConfirmClick={onConfirmClick}
											modalHeaderLabel={labels.modalHeaderLabel}
											confirmationMessage={labels.confirmationMessage}
											confirmButtonLabel={labels.confirmButtonLabel}
											cancelButtonLabel={labels.cancelButtonLabel}
										></TicketModal>
									) : (
										''
									)}
								</section>
							</section>
						) : (
							<section className="noresult">
								{showNoResult ? labels.cacheKeyNotFound : ''}
							</section>
						)}
					</section>
				</section>
			) : (
				<section className="message mainResponsiveWrapper">
					<AccessDeniedMessage
						permissionMesage={labels.insufficientPermission}
					/>
				</section>
			)}
		</Wrapper>
	);
}
export default CacheDetails;
